import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import PeopleIcon from '@mui/icons-material/People';
import RepeatIcon from '@mui/icons-material/Repeat';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link } from 'react-router-dom';
import { theme } from '../../theme/colors';
import { isRecurringEvent, formatRecurrenceText } from '../../utils/eventEditAccess';
import { isExternalEvent, getExternalUrl } from '../../utils/externalEvents';

function formatEventDate(start, end) {
  if (!start) return '';
  const s = new Date(start);
  const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  let str = s.toLocaleDateString('en-US', opts);
  if (end) {
    const e = new Date(end);
    const sameDay = s.toDateString() === e.toDateString();
    if (sameDay) {
      str += ` – ${e.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      str += ` – ${e.toLocaleDateString('en-US', opts)}`;
    }
  }
  return str;
}

function formatLocation(event) {
  if (event.is_virtual) return 'Virtual Event';
  const parts = [event.location_name, event.city, event.state].filter(Boolean);
  return parts.join(', ') || 'Location TBD';
}

const ctaButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  fontSize: 14,
  borderRadius: 2,
  py: 1.25,
  position: 'relative',
  boxShadow: '0 2px 8px rgba(4,120,87,0.25)',
  background: theme.gradient,
  '&:hover': {
    opacity: 0.95,
    background: theme.gradient,
    boxShadow: '0 4px 12px rgba(4,120,87,0.35)',
  },
  '& .MuiButton-endIcon': {
    position: 'absolute',
    right: 12,
    margin: 0,
  },
};

export default function EventCard({ event }) {
  const dateStr = formatEventDate(event.start_datetime, event.end_datetime);
  const location = formatLocation(event);
  const external = isExternalEvent(event);
  const externalUrl = getExternalUrl(event);
  const isGoing = !external && event.viewer_rsvp_status === 'going';
  const isInterested = !external && event.viewer_rsvp_status === 'interested';
  const attendeeCount = event.attendee_count || event.going_count || 0;
  const hasImage = !!(event.cover_image || event.cover_image_url);
  const isRecurring = !external && isRecurringEvent(event);
  const recurrenceLabel = formatRecurrenceText(event);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'rgba(4,120,87,0.15)',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#fff',
        transition: 'box-shadow 0.2s, transform 0.15s',
        '&:hover': {
          boxShadow: '0 4px 20px rgba(4,120,87,0.12)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      {hasImage && (
        <Box
          sx={{
            width: '100%',
            height: 160,
            backgroundImage: `url(${event.cover_image || event.cover_image_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <Box sx={{ p: 2, flex: 1 }}>
        {/* Badges row */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          {(event.event_type?.display_name || event.event_type?.name || event.event_type_name) && (
            <Chip
              label={event.event_type?.display_name || event.event_type?.name || event.event_type_name}
              size="small"
              sx={{
                backgroundColor: `${theme.primary}12`,
                color: theme.primary,
                fontWeight: 600,
                fontSize: 11,
              }}
            />
          )}
          {isRecurring && (
            <Chip
              icon={<RepeatIcon sx={{ fontSize: 14 }} />}
              label={recurrenceLabel}
              size="small"
              variant="outlined"
              sx={{ fontSize: 11, borderColor: '#94a3b8', color: '#64748b' }}
            />
          )}
          {isGoing && (
            <Chip label="Going" size="small" sx={{ backgroundColor: theme.primary, color: '#fff', fontWeight: 600, fontSize: 11 }} />
          )}
          {isInterested && !isGoing && (
            <Chip label="Interested" size="small" variant="outlined" sx={{ borderColor: theme.primary, color: theme.primary, fontWeight: 600, fontSize: 11 }} />
          )}
        </Box>

        {/* Title */}
        <Typography
          variant="subtitle1"
          fontWeight={700}
          sx={{ mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
        >
          {event.title}
        </Typography>

        {/* Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, color: '#334155' }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: theme.primary }} />
          <Typography variant="body2" fontSize={13}>{dateStr}</Typography>
        </Box>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, color: '#334155' }}>
          {event.is_virtual ? (
            <VideocamIcon sx={{ fontSize: 16, color: theme.primary }} />
          ) : (
            <LocationOnIcon sx={{ fontSize: 16, color: theme.primary }} />
          )}
          <Typography variant="body2" fontSize={13}>{location}</Typography>
        </Box>

        {/* Attendee count — native events only */}
        {!external && attendeeCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: '#64748b' }}>
            <PeopleIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption">{attendeeCount} attending</Typography>
          </Box>
        )}
      </Box>

      {/* CTA: external → Mobilize in new tab; native → detail page */}
      <Box sx={{ p: 2, pt: 0 }}>
        {external && (
          <Box
            sx={{
              mb: 1.25,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #0f172a',
            }}
          >
            <Typography sx={{ color: '#0f172a', fontSize: 12, fontWeight: 600, lineHeight: 1.4 }}>
              Hosted on {event.external_organization_name ? `Mobilize by ${event.external_organization_name}` : 'Mobilize'} — not all CivixThread features are available
            </Typography>
          </Box>
        )}
        {external && externalUrl ? (
          <Button
            component="a"
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            fullWidth
            sx={ctaButtonSx}
            endIcon={<OpenInNewIcon sx={{ fontSize: 20 }} />}
          >
            View on Mobilize
          </Button>
        ) : (
          <Button
            component={Link}
            to={`/events/${event.id}`}
            variant="contained"
            fullWidth
            sx={ctaButtonSx}
            endIcon={<ChevronRightIcon sx={{ fontSize: 32 }} />}
          >
            Event Details
          </Button>
        )}
      </Box>
    </Box>
  );
}
